"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.isAlreadyUpdated = exports.useCollectionQuery = void 0;
const client_1 = require("@apollo/client");
const cloneDeep_1 = __importDefault(require("lodash/cloneDeep"));
const react_1 = require("react");
const formatters_1 = require("@jobber/formatters");
const uniqueNodes_1 = require("./uniqueNodes");
const uniqueEdges_1 = require("./uniqueEdges");
const useIsMounted_1 = require("../useIsMounted");
function useCollectionQuery({ query, queryOptions, getCollectionByPath, subscription, }) {
    var _a, _b;
    const { data, loading, refetch, error, fetchMore, subscribeToMore } = (0, client_1.useQuery)(query, queryOptions);
    const isMounted = (0, useIsMounted_1.useIsMounted)();
    const [loadingRefresh, setLoadingRefresh] = (0, react_1.useState)(false);
    const [loadingNextPage, setLoadingNextPage] = (0, react_1.useState)(false);
    const [hookError, setHookError] = (0, react_1.useState)();
    const loadingInitialContent = loading && !loadingRefresh && !loadingNextPage;
    const isSearching = !!((_a = queryOptions === null || queryOptions === void 0 ? void 0 : queryOptions.variables) === null || _a === void 0 ? void 0 : _a.searchTerm);
    const refresh = (0, react_1.useCallback)(() => {
        if (loadingInitialContent || loadingRefresh) {
            return;
        }
        setLoadingRefresh(true);
        fetchMore({
            // a workaround fix for the error described in this post
            // https://github.com/apollographql/apollo-client/issues/7491#issuecomment-767985363
            // These changes can be reverted once we can update to version 3.4
            // (the current release candidate)
            variables: {},
            updateQuery: (prev, { fetchMoreResult }) => fetchMoreResult || prev,
        })
            .catch(err => formatters_1.config.errorNotifier("Refetch Error", err))
            .finally(() => {
            if (isMounted.current) {
                setLoadingRefresh(false);
            }
        });
    }, [
        loadingInitialContent,
        loadingRefresh,
        setLoadingRefresh,
        refetch,
        isMounted,
    ]);
    const nextPage = (0, react_1.useCallback)(() => {
        var _a;
        if (loadingInitialContent || loadingRefresh || loadingNextPage) {
            return;
        }
        const pageInfo = (_a = getCollectionByPath(data)) === null || _a === void 0 ? void 0 : _a.pageInfo;
        if (!pageInfo || !pageInfo.hasNextPage) {
            return;
        }
        setLoadingNextPage(true);
        setHookError(undefined);
        fetchMore({
            variables: {
                cursor: pageInfo.endCursor,
            },
            updateQuery: (prev, { fetchMoreResult }) => fetchMoreUpdateQueryHandler(prev, fetchMoreResult, getCollectionByPath),
        })
            .catch(err => {
            formatters_1.config.errorNotifier("FetchMore Error", err);
            setHookError(err);
        })
            .finally(() => {
            if (isMounted.current) {
                setLoadingNextPage(false);
            }
        });
    }, [
        data,
        loadingInitialContent,
        loadingRefresh,
        fetchMore,
        loadingNextPage,
        setLoadingNextPage,
        getCollectionByPath,
        isMounted,
    ]);
    (0, react_1.useEffect)(() => {
        if (subscription == undefined)
            return;
        const subscriptionOptions = subscription.options || {};
        // Reset this state so we can handle errors from the subscription
        setHookError(undefined);
        return subscribeToMore(Object.assign(Object.assign({}, subscriptionOptions), { document: subscription.document, updateQuery: (prev, { subscriptionData }) => subscribeToMoreHandler(isSearching, prev, getCollectionByPath, subscriptionData === null || subscriptionData === void 0 ? void 0 : subscriptionData.data, subscription.getNodeByPath), onError: err => {
                formatters_1.config.errorNotifier("Subscribe to More Error", err);
                setHookError(err);
            } }));
    }, 
    // Disabling this linter so we can force this only run once. If we didn't
    // do this we would need to ensure subscription, subscribeToMore, and getNodeByPath
    // all use useCallback.
    [(_b = queryOptions === null || queryOptions === void 0 ? void 0 : queryOptions.variables) === null || _b === void 0 ? void 0 : _b.searchTerm]);
    const combinedError = error || hookError;
    return {
        data,
        error: combinedError,
        refresh,
        loadingRefresh,
        nextPage,
        loadingNextPage,
        loadingInitialContent,
    };
}
exports.useCollectionQuery = useCollectionQuery;
/**
 * The following method uses an `output` variable, and indirectly modifies it through the `outputCollection` variable.
 * This type of indirect modification is prone to bugs, but I couldn't think of a better way to write this code.
 *
 * Here's what I was balancing:
 * 1. We need to copy the structure of prev to ensure that when we're combining two objects, we're not losing properties
 * 2. We need to extract a key from an unknown path that's given to us by getCollectionByPath and then we need to modify
 *    that, and ensure that it's properly set on the cloned output object.
 * 3. We want to keep the interface to this hook as simple and easy to use as possible
 *
 * The alternative approaches that were rejected:
 * 1. We replace the getCollectionByPath with a keyPath string. (Eg. "data.conversation.message") and then we use lodash
 *    `get` and `set` which should remove all the object manipulation. But, this approach loses us the type safety that
 *    getCollectionByPath gives us
 * 2. We could add a setCollection function to the list of arguments for this hook. This leaves us with type safety but
 *    makes the `useCollectionQuery` interface more complicated by adding arguments
 */
function fetchMoreUpdateQueryHandler(prev, fetchMoreResult, getCollectionByPath) {
    const nextCollection = getCollectionByPath(fetchMoreResult);
    const output = (0, cloneDeep_1.default)(prev);
    const outputCollection = getCollectionByPath(output);
    if (outputCollection === undefined || nextCollection === undefined) {
        return output;
    }
    if (outputCollection.nodes && nextCollection.nodes) {
        outputCollection.nodes = getUpdatedNodes(outputCollection.nodes, nextCollection.nodes);
    }
    if (outputCollection.edges && nextCollection.edges) {
        outputCollection.edges = getUpdatedEdges(outputCollection.edges, nextCollection.edges);
    }
    Object.assign(outputCollection, Object.assign({ pageInfo: (0, cloneDeep_1.default)(nextCollection.pageInfo) }, getTotalCount(nextCollection.totalCount)));
    return output;
}
function subscribeToMoreHandler(isSearching, prev, getCollectionByPath, subscriptionData, getNodeByPath) {
    const node = getNodeByPath(subscriptionData);
    const output = (0, cloneDeep_1.default)(prev);
    const outputCollection = getCollectionByPath(output);
    if (outputCollection == undefined || node == undefined)
        return output;
    if (isAlreadyUpdated(outputCollection, node) || isSearching) {
        return prev;
    }
    if (outputCollection.nodes) {
        outputCollection.nodes = getUpdatedNodes(outputCollection.nodes, [node], false);
    }
    if (outputCollection.edges) {
        outputCollection.edges = getUpdatedEdges(outputCollection.edges, [(0, uniqueEdges_1.createEdge)(node)], false);
    }
    Object.assign(outputCollection, Object.assign({ pageInfo: (0, cloneDeep_1.default)(outputCollection.pageInfo) }, getTotalCount(outputCollection.totalCount, 1)));
    return output;
}
function getTotalCount(totalCount, additionalCount = 0) {
    return totalCount !== undefined
        ? { totalCount: totalCount + additionalCount }
        : {};
}
function isAlreadyUpdated(outputCollection, newNode) {
    let edgesAlreadyUpdated = true;
    let nodesAlreadyUpdated = true;
    if (outputCollection.edges) {
        edgesAlreadyUpdated = outputCollection.edges.some(edge => {
            return edge.node.id === newNode.id;
        });
    }
    if (outputCollection.nodes) {
        nodesAlreadyUpdated = outputCollection.nodes.some(node => {
            return node.id === newNode.id;
        });
    }
    return edgesAlreadyUpdated && nodesAlreadyUpdated;
}
exports.isAlreadyUpdated = isAlreadyUpdated;
function getUpdatedEdges(prevEdges, nextEdges, appendToEnd = true) {
    const newEdges = appendToEnd
        ? [...prevEdges, ...nextEdges]
        : [...nextEdges, ...prevEdges];
    return (0, uniqueEdges_1.uniqueEdges)(newEdges);
}
function getUpdatedNodes(prevNodes, nextNodes, appendToEnd = true) {
    const newNodes = appendToEnd
        ? [...prevNodes, ...nextNodes]
        : [...nextNodes, ...prevNodes];
    return (0, uniqueNodes_1.uniqueNodes)(newNodes);
}
